import { HeaderCellProps } from '@/shared/types/table';
import { cn } from '@/shared/utils/cn';
import { InnerButton } from '@/modules/InnerButton';
import { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/modules/Icon';
import {
  faFilter,
  faUnlock,
  faLock,
  faArrowUp,
  faArrowDown,
  faSort,
  faArrowDownArrowUp,
  faArrowsFromLine,
  faArrowsDownToLine,
} from '@fortawesome/pro-regular-svg-icons';
import {
  faLock as faLockSolid,
  faArrowUp as faArrowUpSolid,
  faArrowDown as faArrowDownSolid,
  faFilter as faFilterSolid,
} from '@fortawesome/pro-solid-svg-icons';
import { Tooltip } from '@/modules/Tooltip';
import { GHANTT_TIMELINE_HEADER_STACK_HEIGHT_PX } from '@/shared/ghantt';
import {
  DEFAULT_ACTIONS_COLUMN_WIDTH,
  DEFAULT_MIN_COLUMN_WIDTH,
  DEFAULT_FLEX_COLUMN_MIN_WIDTH,
  isFlexColumn,
} from '../../utils/cellStyles';

export const HeaderCell = ({
  column,
  sortable = true,
  filterable = true,
  lockable = true,
  resizable = true,
  sortDirection = null,
  filterActive = false,
  locked = false,
  isLastLocked = false,
  stickyLeft = 0,
  stickyRight,
  stickyHeader = true,
  isActionsColumn = false,
  onSort,
  onFilter,
  onLock,
  onResize,
  onResizeDoubleClick,
  onResizeHover,
  rowsDataKey,
  className,
  hasColumnMaxWidthAuto = false,
}: HeaderCellProps) => {
  const cellRef = useRef<HTMLTableCellElement>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [hasMovedThreshold, setHasMovedThreshold] = useState(false);
  const [isHoveringResize, setIsHoveringResize] = useState(false);
  const startXRef = useRef(0);
  const adjustedStartXRef = useRef(0);
  const startWidthRef = useRef(0);
  const minWidthRef = useRef(80);
  const rafIdRef = useRef<number | null>(null);
  const onResizeRef = useRef(onResize);
  const columnIdRef = useRef(column.id);
  const hasMovedThresholdRef = useRef(false);
  const handleResizeMoveRef = useRef<(e: MouseEvent) => void>();
  const handleResizeEndRef = useRef<() => void>();
  const resizeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    onResizeRef.current = onResize;
    columnIdRef.current = column.id;
    hasMovedThresholdRef.current = hasMovedThreshold;
  }, [onResize, column.id, hasMovedThreshold]);

  const hasWidth = column.width !== undefined;
  const isActionsNoWidth = isActionsColumn && !hasWidth;
  const effectiveMinForAuto = column.minWidth ?? DEFAULT_MIN_COLUMN_WIDTH;
  const defaultActionsWidth = isActionsNoWidth
    ? (column.minWidth ?? DEFAULT_ACTIONS_COLUMN_WIDTH)
    : 0;

  let width: number | string | undefined;
  let minWidth: number;
  if (isActionsNoWidth) {
    width = defaultActionsWidth;
    minWidth = defaultActionsWidth;
  } else if (hasColumnMaxWidthAuto) {
    if (
      column.maxWidth === 'auto' &&
      (column.width === undefined || typeof column.width !== 'number')
    ) {
      width = undefined;
      minWidth = column.minWidth ?? DEFAULT_FLEX_COLUMN_MIN_WIDTH;
    } else {
      width =
        typeof column.width === 'number' || typeof column.width === 'string'
          ? column.width
          : effectiveMinForAuto;
      minWidth = column.minWidth ?? (column.width !== undefined ? 0 : DEFAULT_MIN_COLUMN_WIDTH);
    }
  } else {
    if (
      column.maxWidth === 'auto' &&
      (column.width === undefined || typeof column.width !== 'number')
    ) {
      width = '100%';
      minWidth = column.minWidth ?? 0;
    } else if (hasWidth) {
      width = column.width;
      minWidth = column.minWidth ?? 0;
    } else {
      width = effectiveMinForAuto;
      minWidth = effectiveMinForAuto;
    }
  }

  const resolvedNum =
    typeof column.width === 'number'
      ? column.width
      : typeof column.width === 'string'
        ? parseFloat(column.width)
        : NaN;
  const maxWidth = isActionsNoWidth
    ? defaultActionsWidth
    : column.maxWidth === 'auto'
      ? undefined
      : (column.maxWidth ?? (Number.isFinite(resolvedNum) ? resolvedNum : undefined));

  const shouldTruncate = hasWidth || isFlexColumn(column, hasColumnMaxWidthAuto);

  const isGanttColumn = column.type === 'gantt';

  const baseClasses = cn(
    'ui-group ui-text-left ui-text-body-xs-regular ui-font-body-xs-regular ui-leading-body-xs-regular ui-tracking-body-xs ui-text-tertiary',
    'ui-transition-colors ui-duration-150',
    isGanttColumn
      ? 'ui-box-border ui-h-auto ui-px-0 ui-py-0 ui-align-top'
      : 'ui-h-12 ui-px-4 ui-py-3',
    'ui-border-b ui-border-default',
    // Fondo para todas las celdas
    'ui-bg-surface',
    locked && 'ui-sticky ui-z-[51] ui-isolate',
    locked && stickyHeader && 'ui-top-0',
    isActionsColumn && 'ui-sticky ui-z-[51] ui-isolate ui-border-l ui-border-l-default',
    isActionsColumn && stickyHeader && 'ui-top-0',
    isLastLocked && 'ui-border-r-2 ui-border-r-default',
    className,
  );

  const getSortIcon = (isSorted: boolean) => {
    if (sortDirection === 'asc') return isSorted ? faArrowUpSolid : faArrowUp;
    if (sortDirection === 'desc') return isSorted ? faArrowDownSolid : faArrowDown;
    return faArrowDownArrowUp;
  };

  // Determinar si los botones deben estar siempre visibles
  const hasActiveState = sortDirection !== null || filterActive || locked;

  const buttonContainerClasses = cn(
    'ui-flex ui-items-center ui-gap-1',
    'ui-transition-opacity ui-duration-200',
    !hasActiveState && 'ui-opacity-0 group-hover:ui-opacity-100',
  );

  // Lógica de resize
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!cellRef.current || !onResize) return;

    setIsResizing(true);
    setHasMovedThreshold(false);
    hasMovedThresholdRef.current = false;
    // Usar la posición del click como referencia para el movimiento
    startXRef.current = e.clientX;
    adjustedStartXRef.current = e.clientX;

    // Usar el ancho actual de la columna desde el prop o desde el DOM
    const currentWidth =
      typeof column.width === 'number' ? column.width : cellRef.current.offsetWidth;

    // Asegurar que currentWidth es un número válido
    if (isNaN(currentWidth) || currentWidth <= 0) {
      startWidthRef.current = 150;
    } else {
      startWidthRef.current = currentWidth;
    }

    // Calcular ancho mínimo basándose en el contenido del header Y las celdas de datos
    let maxContentWidth = 0;

    // 1. Medir el ancho del contenido del header
    const headerTextElement = cellRef.current.querySelector('.ui-truncate') as HTMLElement;
    let headerWidth = 0;

    if (headerTextElement?.textContent?.trim()) {
      // Crear un elemento temporal para medir el texto sin truncar
      const tempSpan = document.createElement('span');
      tempSpan.style.position = 'absolute';
      tempSpan.style.visibility = 'hidden';
      tempSpan.style.whiteSpace = 'nowrap';
      tempSpan.style.font = window.getComputedStyle(headerTextElement).font;
      tempSpan.textContent = headerTextElement.textContent;
      document.body.appendChild(tempSpan);
      headerWidth = tempSpan.offsetWidth;
      document.body.removeChild(tempSpan);
    }

    // Medir el icono del header desde el DOM (si existe)
    const outerFlex = cellRef.current.querySelector('.ui-justify-between');
    const innerFlex = outerFlex?.firstElementChild as HTMLElement;
    const headerIconEl = column.headerIcon
      ? (innerFlex?.querySelector('.ui-flex-shrink-0') as HTMLElement)
      : null;
    if (headerIconEl && headerIconEl.offsetWidth > 0) {
      headerWidth += headerIconEl.offsetWidth + 8; // icono + gap
    }

    // Medir el ancho real del contenedor de botones usando la estructura del DOM
    const buttonsContainer = outerFlex?.lastElementChild as HTMLElement;
    const buttonsWidth = buttonsContainer ? buttonsContainer.offsetWidth : 0;

    const headerTotalWidth = headerWidth + buttonsWidth + 48; // +48 = 32px padding + 8px gap + 8px margen subpixel
    if (headerWidth > 0 || buttonsWidth > 0) {
      maxContentWidth = Math.max(maxContentWidth, headerTotalWidth);
    }

    // 2. Medir las celdas de datos usando scrollWidth (mismo enfoque que double-click resize)
    const table = cellRef.current.closest('table');
    let maxCellScrollWidth = 0;

    if (table) {
      const dataCells = table.querySelectorAll(`td[data-column-id="${column.id}"]`);
      dataCells.forEach((cell) => {
        const cellScrollWidth = (cell as HTMLElement).scrollWidth;
        maxCellScrollWidth = Math.max(maxCellScrollWidth, cellScrollWidth);
      });
    }

    const cellNeededWidth = maxCellScrollWidth > 0 ? maxCellScrollWidth + 32 : 0;
    maxContentWidth = Math.max(maxContentWidth, cellNeededWidth);

    // El ancho mínimo calculado basado en el contenido
    const calculatedMinWidth = Math.max(80, maxContentWidth || 80);
    // Incluir el ancho original de la columna (definido al montar) para poder volver a él al reducir
    const originalWidth =
      typeof column.originalWidth === 'number' ? column.originalWidth : undefined;
    // El usuario debe poder reducir al menos hasta el ancho original; el mínimo permitido es el menor de los tres
    minWidthRef.current = Math.min(
      startWidthRef.current,
      calculatedMinWidth,
      originalWidth ?? Infinity,
    );
  };

  const updateResizeButtonPosition = useCallback(() => {
    if (!resizeButtonRef.current || !cellRef.current) return;
    const rect = cellRef.current.getBoundingClientRect();
    resizeButtonRef.current.style.top = `${rect.bottom - 16}px`;
    resizeButtonRef.current.style.left = `${rect.right - 16}px`;
  }, []);

  const handleResizeMove = useCallback(
    (e: MouseEvent) => {
      if (!onResizeRef.current) return;

      const diff = e.clientX - startXRef.current;
      const moved = Math.abs(diff);

      // Umbral de 3px para evitar ajustes accidentales al hacer click
      const currentHasMoved = hasMovedThresholdRef.current;
      if (!currentHasMoved && moved < 3) {
        return;
      }

      // Activar el threshold sin ajustar el punto de inicio
      if (!currentHasMoved) {
        hasMovedThresholdRef.current = true;
        setHasMovedThreshold(true);
      }

      // Calcular el diff desde el punto inicial del click
      const adjustedDiff = e.clientX - adjustedStartXRef.current;

      // Asegurar que los valores son números válidos
      const safeStartWidth =
        isNaN(startWidthRef.current) || startWidthRef.current <= 0 ? 150 : startWidthRef.current;
      const safeMinWidth =
        isNaN(minWidthRef.current) || minWidthRef.current <= 0 ? 80 : minWidthRef.current;
      const newWidth = Math.max(safeMinWidth, safeStartWidth + adjustedDiff);

      // No continuar si newWidth no es válido
      if (isNaN(newWidth) || newWidth <= 0) {
        return;
      }

      // Usar requestAnimationFrame para hacer el resize más fluido y sincronizado
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }

      rafIdRef.current = requestAnimationFrame(() => {
        onResizeRef.current?.(newWidth, startWidthRef.current);
        updateResizeButtonPosition();
      });
    },
    [updateResizeButtonPosition],
  );

  // Mantener la referencia actualizada
  useEffect(() => {
    handleResizeMoveRef.current = handleResizeMove;
  }, [handleResizeMove]);

  const handleResizeEnd = useCallback(() => {
    setIsResizing(false);
    setHasMovedThreshold(false);
    hasMovedThresholdRef.current = false;
    setIsHoveringResize(false);
    onResizeHover?.(false);
    adjustedStartXRef.current = 0;

    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  }, [onResizeHover]);

  // Mantener la referencia actualizada
  useEffect(() => {
    handleResizeEndRef.current = handleResizeEnd;
  }, [handleResizeEnd]);

  const handleResizeDoubleClickInternal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onResizeDoubleClick?.();
  };

  const handleResizeMouseEnter = () => {
    setIsHoveringResize(true);
    onResizeHover?.(true);
    requestAnimationFrame(updateResizeButtonPosition);
  };

  const handleResizeMouseLeave = () => {
    if (!isResizing) {
      setIsHoveringResize(false);
      onResizeHover?.(false);
    }
  };

  // Agregar event listeners globales para el resize
  useEffect(() => {
    if (isResizing) {
      const moveHandler = (e: MouseEvent) => handleResizeMoveRef.current?.(e);
      const endHandler = () => handleResizeEndRef.current?.();

      document.addEventListener('mousemove', moveHandler);
      document.addEventListener('mouseup', endHandler);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      return () => {
        document.removeEventListener('mousemove', moveHandler);
        document.removeEventListener('mouseup', endHandler);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';

        // Cancelar cualquier animación pendiente
        if (rafIdRef.current) {
          cancelAnimationFrame(rafIdRef.current);
          rafIdRef.current = null;
        }
      };
    }
  }, [isResizing]);

  return (
    <th
      ref={cellRef}
      data-column-id={column.id}
      className={cn(
        baseClasses,
        'ui-relative',
        shouldTruncate && !isGanttColumn && 'ui-overflow-hidden',
        // Sticky sin z-index: el tbody pinta encima del thead (orden DOM). z-30 alinea con checkbox en TableHeader.
        // Columnas locked/actions llevan z-[51] en baseClasses.
        stickyHeader && !locked && !isActionsColumn && 'ui-sticky ui-top-0 ui-isolate ui-z-[30]',
      )}
      style={{
        minWidth,
        maxWidth,
        width,
        ...(locked && { left: stickyLeft }),
        ...(isActionsColumn && stickyRight !== undefined && { right: stickyRight }),
        ...(isGanttColumn && { minHeight: GHANTT_TIMELINE_HEADER_STACK_HEIGHT_PX }),
      }}
    >
      {isGanttColumn ? (
        <div className="ui-box-border ui-flex ui-h-full ui-min-h-0 ui-w-full ui-min-w-0 ui-flex-col">
          {column.header}
        </div>
      ) : (
        <div
          className={cn(
            'ui-flex ui-items-center ui-justify-between ui-gap-2',
            shouldTruncate && 'ui-min-w-0 ui-overflow-hidden',
          )}
        >
          <div className="ui-flex ui-min-w-0 ui-items-center ui-gap-8">
            {column.headerIcon && (
              <Icon
                name={column.headerIcon}
                size="sm"
                color="default"
                className="ui-flex-shrink-0"
              />
            )}
            {typeof column.header === 'string' ? (
              <span className="ui-truncate ui-uppercase">{column.header}</span>
            ) : (
              column.header
            )}
          </div>

          <div className="ui-flex ui-flex-shrink-0 ui-items-center ui-gap-1">
            {/* Icono de bloqueo - siempre visible si está activo, en hover si no */}
            {lockable && (
              <div
                className={cn(
                  locked ? 'ui-opacity-100' : 'ui-opacity-0 group-hover:ui-opacity-100',
                  'ui-transition-opacity ui-duration-200',
                )}
              >
                <InnerButton
                  theme="solid"
                  color="tertiary"
                  size="xxs"
                  pill="xs"
                  iconProps={{
                    icon: locked ? faLockSolid : faUnlock,
                    color: locked ? 'blue' : 'default',
                  }}
                  onClick={onLock}
                  tooltipProps={{
                    label: locked ? 'Desbloquear columna' : 'Bloquear columna',
                    position: 'top-left',
                  }}
                />
              </div>
            )}

            {/* Icono de filtro - siempre visible si está activo, en hover si no */}
            {filterable && column.filterable !== false && (
              <div
                className={cn(
                  filterActive ? 'ui-opacity-100' : 'ui-opacity-0 group-hover:ui-opacity-100',
                  'ui-transition-opacity ui-duration-200',
                )}
              >
                <Tooltip label={filterActive ? '"Filtros aplicados"' : ''}>
                  <Icon
                    size="xs"
                    name={filterActive ? faFilterSolid : faFilter}
                    color={filterActive ? 'blue' : 'default'}
                  />
                </Tooltip>
              </div>
            )}

            {/* Icono de ordenamiento - siempre visible si está activo, en hover si no */}
            {sortable && column.sortable !== false && (
              <div
                className={cn(
                  sortDirection !== null
                    ? 'ui-opacity-100'
                    : 'ui-opacity-0 group-hover:ui-opacity-100',
                  'ui-transition-opacity ui-duration-200',
                )}
              >
                <InnerButton
                  theme="solid"
                  color="tertiary"
                  iconProps={{
                    icon: getSortIcon(sortDirection !== null),
                    color: sortDirection !== null ? 'brand' : 'default',
                  }}
                  size="xxs"
                  pill="xs"
                  onClick={onSort}
                  tooltipProps={{ label: 'Ordenar' }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Resize Handle: 9px en esta columna + 9px en la vecina (inicio de columna siguiente) para activar desde ambos lados del borde */}
      {resizable && column.resizable !== false && !locked && (
        <div
          className="ui-absolute ui-right-[-9px] ui-top-0 ui-z-10 ui-h-full ui-w-[18px] ui-cursor-col-resize"
          onMouseDown={handleResizeStart}
          onMouseEnter={handleResizeMouseEnter}
          onMouseLeave={handleResizeMouseLeave}
        />
      )}

      {/* Botón de resize via Portal — fuera del DOM de la tabla para evitar artefactos */}
      {resizable &&
        column.resizable !== false &&
        !locked &&
        (isHoveringResize || isResizing) &&
        createPortal(
          <button
            ref={resizeButtonRef}
            className={cn(
              'ui-fixed ui-flex ui-rotate-90 ui-items-center ui-justify-center',
              'ui-h-[32px] ui-w-[32px]',
              'ui-border ui-border-solid ui-border-default ui-bg-fill-secondary',
              'ui-rounded-[9999px]',
              'ui-cursor-col-resize',
              'hover:ui-bg-fill-secondary-hover',
              'ui-z-[9999]',
            )}
            style={{
              top: (cellRef.current?.getBoundingClientRect().bottom ?? 0) - 16,
              left: (cellRef.current?.getBoundingClientRect().right ?? 0) - 16,
            }}
            onMouseDown={handleResizeStart}
            onMouseEnter={handleResizeMouseEnter}
            onMouseLeave={handleResizeMouseLeave}
            onDoubleClick={handleResizeDoubleClickInternal}
            onClick={(e) => e.preventDefault()}
          >
            <Icon name={faArrowsFromLine} color="default" size="sm" />
          </button>,
          document.body,
        )}
    </th>
  );
};
